import { t } from "@/i18n-js/instance";
import { StandardLayoutHeader } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeader";
import { SpaceHeaderTitleText } from "@circle-react/components/Spaces/Header/SpaceHeaderTitle/SpaceHeaderTitleText";
import { usePunditUserContext } from "@circle-react/contexts";
import { hasRevenueCatPaymentProcessor } from "@circle-react/helpers/paymentProcessorHelpers";
import { useCommunityPaymentProcessorStatus } from "@circle-react/hooks/communityPaymentProcessor/useCommunityPaymentProcessorStatus";
import { PaywallsEmptyState } from "@circle-react-shared/Paywalls/PaywallsEmptyState";
import { Loader } from "@circle-react-uikit/Loader";
import { Filters } from "../Filters";
import type { FILTERS_AVAILABLE } from "../Filters/useFilters";
import { useIsFilterBeingUsedOnSubscriptions } from "../Filters/useIsFilterBeingUsed";
import { HeaderRightContent } from "./HeaderRightContent";
import { Headline } from "./Headline";
import { SubscriptionList } from "./SubscriptionList";
import { TabWithFilters } from "./TabWithFilters";
import { TABS } from "./constants";
import type { TabType } from "./constants";
import { usePaywallSubscriptionsAdmin } from "./hooks/usePaywallSubscriptionsAdmin";

const customFilters: Array<Partial<FILTERS_AVAILABLE>> = [
  "total_amount_paid",
  "currency",
  "community_member_billing_info_business_name",
];

export const PaywallSubscriptionsAdmin = () => {
  const {
    subscriptionsDataByTab,
    resetAllSubscriptionToFirstPage,
    activeFilterTab,
    setActiveFilterTab,
    refetchAllSubscriptions,
    setSearchKey,
    isLoadingSubscriptions,
  } = usePaywallSubscriptionsAdmin();

  const { currentCommunity } = usePunditUserContext();
  const isFilterBeingUsed = useIsFilterBeingUsedOnSubscriptions();

  const activeFilterTabValue: TabType = activeFilterTab || TABS.ALL;

  const totalSubscriptionsCount =
    subscriptionsDataByTab[activeFilterTabValue]?.totalSubscriptions;

  const { isPaymentProcessorEnabled } = useCommunityPaymentProcessorStatus();

  const platformFilterToRender: Array<Partial<FILTERS_AVAILABLE>> =
    hasRevenueCatPaymentProcessor(currentCommunity?.payment_processors)
      ? ["platform"]
      : [];

  const filters: Array<Partial<FILTERS_AVAILABLE>> = [
    "community_member_name",
    "community_member_email",
    "community_member_id",
    "subscription_status",
    "paywalls",
    ...platformFilterToRender,
    "start_date",
    "subscription_id",
  ];

  if (isLoadingSubscriptions) {
    return (
      <div className="flex h-full">
        <Loader center />
      </div>
    );
  }

  if (
    !isLoadingSubscriptions &&
    !isFilterBeingUsed &&
    (totalSubscriptionsCount === 0 || !isPaymentProcessorEnabled)
  ) {
    return (
      <PaywallsEmptyState
        isPaymentProcessorEnabled={isPaymentProcessorEnabled}
        i18nBase="settings.paywalls_admin_dashboard.subscriptions"
        redirectToPaywalls
      />
    );
  }

  return (
    <>
      <StandardLayoutHeader
        leftContent={
          <SpaceHeaderTitleText>
            {t("settings.paywalls_admin_dashboard.subscriptions.title")}
          </SpaceHeaderTitleText>
        }
        rightContent={
          <HeaderRightContent
            totalSubscriptionsCount={totalSubscriptionsCount}
          />
        }
        filters={
          <TabWithFilters
            setActiveFilterTab={setActiveFilterTab}
            activeFilterTab={activeFilterTabValue}
            setSearchKey={setSearchKey}
            resetAllSubscriptionToFirstPage={resetAllSubscriptionToFirstPage}
          />
        }
        hasBorder={false}
      />

      <Filters filterNames={filters} customFilterNames={customFilters} />
      <Headline totalSubscriptionsCount={totalSubscriptionsCount} />

      <SubscriptionList
        activeFilterTab={activeFilterTabValue}
        subscriptionsDataByTab={subscriptionsDataByTab}
        refetchAllSubscriptions={refetchAllSubscriptions}
      />
    </>
  );
};
